import React, { useState, useEffect } from "react";
import { FileUploaderPreview } from "../../components/FileUploaderPreview";
import { EvidenciaImg } from "../../types/ot.types";

interface ProveedorAltaData {
  clave_persona: string;
  clave_personalidad_fiscal: string;
  clave_periodo_pago: string;
  enum_forma_pago: string;
  nombre: string;
  apellido_paterno: string;
  apellido_materno: string;
  razon_social: string;
  direccion: string;
  colonia: string;
  codigo_postal: string;
  clave_estado: string;
  clave_ciudad: string;
  rfc: string;
  clave_estatus: string;
  clave_banco?: string;
  clabe?: string;
  constancia_fiscal_url?: string;
  caratula_estado_cuenta_url?: string;
}

interface Catalogo {
  id: string | number;
  nombre: string;
}
interface Estado {
  clave_estado: number | string;
  clave_pais: number | string;
  estado: string;
}
interface Ciudad {
  clave_ciudad: number | string;
  ciudad: string;
  clave_estado: number | string;
}

const ESTATUS_OPTIONS = [
  { id: "ACTI", label: "Activo", color: "success" },
  { id: "PEND", label: "Nuevo a prueba", color: "primary" },
  { id: "REVI", label: "A prueba por servicio", color: "warning" },
  { id: "BLOQ", label: "Bloqueado", color: "danger" },
  { id: "CANC", label: "Eliminado / Dado de baja", color: "secondary" },
];

const ProveedoresAlta: React.FC = () => {
  // --- Estados de Datos ---
  const [proveedor, setProveedor] = useState<ProveedorAltaData>({
    clave_persona: "",
    clave_personalidad_fiscal: "",
    clave_periodo_pago: "",
    enum_forma_pago: "",
    nombre: "",
    apellido_paterno: "",
    apellido_materno: "",
    razon_social: "",
    direccion: "",
    colonia: "",
    codigo_postal: "",
    clave_estado: "",
    clave_ciudad: "",
    clabe: "",
    rfc: "",
    clave_estatus: "ACTI",
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(
    null,
  );

  // --- Archivos ---
  const [constanciaFiles, setConstanciaFiles] = useState<EvidenciaImg[]>([]);
  const [caratulaFiles, setCaratulaFiles] = useState<EvidenciaImg[]>([]);

  // --- Catálogos ---
  const [personalidades, setPersonalidades] = useState<Catalogo[]>([]);
  const [periodosPago, setPeriodosPago] = useState<Catalogo[]>([]);
  const [formasPago, setFormasPago] = useState<Catalogo[]>([]);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [ciudades, setCiudades] = useState<Ciudad[]>([]);
  const [bancos, setBancos] = useState<Catalogo[]>([]);

  const fetchSeguro = async (url: string, options?: RequestInit) => {
    const response = await fetch(url, options);
    const data = await response.json();
    if (
      data &&
      data.status === "error" &&
      data.message &&
      data.message.includes("Sesión inválida")
    ) {
      window.location.href = "/";
      throw new Error("Sesión expirada. Redirigiendo...");
    }
    return data;
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (proveedor.clave_estado) fetchCiudades(proveedor.clave_estado);
    else setCiudades([]);
  }, [proveedor.clave_estado]);

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const [catPersonalidad, catPeriodos, catFormas, catEstados, catBancos] =
        await Promise.all([
          fetchSeguro("/sittApi/catalogos/personalidad_fiscal.php").then(
            (res) => res,
          ),
          fetchSeguro("/sittApi/catalogos/periodos_pago.php").then(
            (res) => res,
          ),
          fetchSeguro("/sittApi/catalogos/formas_pago.php").then((res) => res),
          fetchSeguro("/sittApi/obtener_estados.php").then((res) => res),
          fetchSeguro("/sittApi/catalogos/bancos.php")
            .then((res) => res)
            .catch(() => ({ data: [] })),
        ]);

      setPersonalidades(catPersonalidad.data || []);
      setPeriodosPago(catPeriodos.data || []);
      setFormasPago(catFormas.data || []);
      setEstados(catEstados.data || []);
      setBancos(catBancos.data || catBancos || []);
    } catch (error) {
      setAlert({
        type: "danger",
        message: "Error de red al cargar la información de los catalogos.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCiudades = async (idEstado: string) => {
    try {
      const result = await fetchSeguro("/api/catalogos/ciudades.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ accion: "filtered", idEstado: idEstado }),
      });
      setCiudades(result || []);
    } catch (error) {
      console.error("Error al cargar ciudades", error);
    }
  };

  const handleChange = (campo: keyof ProveedorAltaData, valor: string) => {
    setProveedor((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleSaveGeneral = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setAlert(null);
    try {
      const payload = new FormData();

      if (proveedor) {
        Object.keys(proveedor).forEach((key) => {
          const val = (proveedor as any)[key];
          payload.append(
            key,
            val === null || val === undefined ? "" : String(val),
          );
        });
      }

      if (constanciaFiles.length > 0 && constanciaFiles[0].file) {
        payload.append(
          "tax_certificate",
          constanciaFiles[0].file,
          constanciaFiles[0].nombreOriginal,
        );
      }
      if (caratulaFiles.length > 0 && caratulaFiles[0].file) {
        payload.append(
          "bank_cover",
          caratulaFiles[0].file,
          caratulaFiles[0].nombreOriginal,
        );
      }
        const result = await fetchSeguro("/sittApi/proveedores/agregar.php", {
          method: "POST",
          body: payload,
        });

        if (result.status === "success") {
          setAlert({
            type: "success",
            message:
              "Proveedor registrado correctamente.",
          });
          window.location.href = `proveedores?accion=edit&id=${result.idEncrypted}`;
        } else throw new Error(result.message);
    } catch (error: any) {
      setAlert({
        type: "danger",
        message: error.message || "Error al guardar.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !proveedor) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <span className="ms-3 text-muted">
          Cargando información del proveedor...
        </span>
      </div>
    );
  }

  const isFisica = proveedor.clave_personalidad_fiscal === "1";

  return (
    <div className="container-fluid px-0 animate__animated animate__fadeIn">
      {alert && (
        <div
          className={`alert alert-${alert.type} shadow-sm border-0 alert-dismissible fade show`}
        >
          <i
            className={`fas ${alert.type === "success" ? "fa-check-circle" : "fa-exclamation-triangle"} me-2`}
          ></i>
          {alert.message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlert(null)}
          ></button>
        </div>
      )}

      <div className="row g-4">
        <div className="col-xl-9">
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header border-bottom py-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold">
                <i className="fas fa-edit text-primary me-2"></i> Alta Proveedor
                (Matriz)
              </h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSaveGeneral}>
                {/* --- SECCIÓN: GENERAL --- */}
                <h6 className="fw-bold text-secondary text-uppercase border-bottom pb-2 mb-3">
                  General
                </h6>
                <div className="row g-3 mb-4">
                  <div className="col-lg-3">
                    <label className="fw-bold mb-1 small text-muted">
                      Personalidad fiscal
                    </label>
                    <select
                      className="form-select shadow-sm"
                      name="clave_personalidad_fiscal"
                      onChange={(e) =>
                        handleChange(
                          "clave_personalidad_fiscal",
                          e.target.value,
                        )
                      }
                      required
                    >
                      <option value="">Elegir...</option>
                      {personalidades.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-3">
                    <label className="fw-bold mb-1 small text-muted">
                      Periodo de pago
                    </label>
                    <select
                      className="form-select shadow-sm"
                      name="clave_periodo_pago"
                      onChange={(e) =>
                        handleChange("clave_periodo_pago", e.target.value)
                      }
                      required
                    >
                      <option value="">Elegir...</option>
                      {periodosPago.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-3">
                    <label className="fw-bold mb-1 small text-muted">
                      Forma de pago
                    </label>
                    <select
                      className="form-select shadow-sm"
                      name="enum_forma_pago"
                      onChange={(e) =>
                        handleChange("enum_forma_pago", e.target.value)
                      }
                      required
                    >
                      <option value="">Elegir...</option>
                      {formasPago.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row g-3 mb-5">
                  {isFisica ? (
                    <>
                      <div className="col-lg-4">
                        <label className="fw-bold mb-1 small text-muted">
                          Nombre
                        </label>
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          name="nombre"
                          onChange={(e) =>
                            handleChange("nombre", e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="col-lg-4">
                        <label className="fw-bold mb-1 small text-muted">
                          Apellido paterno
                        </label>
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          name="apellido_paterno"
                          onChange={(e) =>
                            handleChange("apellido_paterno", e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="col-lg-4">
                        <label className="fw-bold mb-1 small text-muted">
                          Apellido materno
                        </label>
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          name="apellido_materno"
                          onChange={(e) =>
                            handleChange("apellido_materno", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-lg-4">
                        <label className="fw-bold mb-1 small text-muted">
                          RFC
                        </label>
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          name="rfc"
                          onChange={(e) => handleChange("rfc", e.target.value)}
                          required
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-lg-8">
                        <label className="fw-bold mb-1 small text-muted">
                          Razón social
                        </label>
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          name="razon_social"
                          onChange={(e) =>
                            handleChange("razon_social", e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="col-lg-4">
                        <label className="fw-bold mb-1 small text-muted">
                          RFC
                        </label>
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          name="rfc"
                          onChange={(e) => handleChange("rfc", e.target.value)}
                          required
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* --- SECCIÓN: BANCARIA Y FISCAL (NUEVA) --- */}
                <h6 className="fw-bold text-secondary text-uppercase border-bottom pb-2 mb-3 mt-4">
                  Fiscal y Bancario (Matriz)
                </h6>
                <div className="alert alert-light border small text-muted mb-4">
                  <i className="fas fa-info-circle me-1 text-primary"></i> Esta
                  información será heredada automáticamente a los
                  Centros/Sucursales asociados.
                </div>
                <div className="row g-3 mb-4">
                  <div className="col-lg-6">
                    <label className="fw-bold mb-1 small text-muted">
                      Banco Predeterminado
                    </label>
                    <select
                      className="form-select shadow-sm"
                      name="clave_banco"
                      onChange={(e) =>
                        handleChange("clave_banco", e.target.value)
                      }
                      required
                    >
                      <option value="">Elegir...</option>
                      {bancos.map((b) => (
                        <option
                          key={b.id || (b as any).clave_banco}
                          value={b.id || (b as any).clave_banco}
                        >
                          {b.nombre || (b as any).banco}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-6">
                    <label className="fw-bold mb-1 small text-muted">
                      CLABE Interbancaria
                    </label>
                    <input
                      type="text"
                      className="form-control shadow-sm"
                      name="clabe"
                      onChange={(e) => handleChange("clabe", e.target.value)}
                      maxLength={18}
                      pattern="[0-9]{18}"
                      placeholder="18 dígitos numéricos"
                      required
                    />
                  </div>
                </div>
                <div className="row g-3 mb-5">
                  <div className="col-lg-6">
                    <FileUploaderPreview
                      label="Constancia de Situación Fiscal"
                      archivos={constanciaFiles}
                      setArchivos={setConstanciaFiles}
                      accept="application/pdf"
                      multiple={false}
                    />
                  </div>
                  <div className="col-lg-6">
                    <FileUploaderPreview
                      label="Carátula de Estado de Cuenta"
                      archivos={caratulaFiles}
                      setArchivos={setCaratulaFiles}
                      accept="application/pdf"
                      multiple={false}
                    />
                  </div>
                </div>

                {/* --- SECCIÓN: DIRECCIÓN --- */}
                <h6 className="fw-bold text-secondary text-uppercase border-bottom pb-2 mb-3 mt-4">
                  Dirección Central
                </h6>
                <div className="row g-3 mb-3">
                  <div className="col-lg-6">
                    <label className="fw-bold mb-1 small text-muted">
                      Calle y número
                    </label>
                    <input
                      type="text"
                      className="form-control shadow-sm"
                      name="direccion"
                      onChange={(e) =>
                        handleChange("direccion", e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="fw-bold mb-1 small text-muted">
                      Colonia
                    </label>
                    <input
                      type="text"
                      className="form-control shadow-sm"
                      name="colonia"
                      onChange={(e) => handleChange("colonia", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-lg-2">
                    <label className="fw-bold mb-1 small text-muted">
                      C.P.
                    </label>
                    <input
                      type="number"
                      className="form-control shadow-sm"
                      name="codigo_postal"
                      onChange={(e) =>
                        handleChange("codigo_postal", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-lg-4">
                    <label className="fw-bold mb-1 small text-muted">
                      Estado
                    </label>
                    <select
                      className="form-select shadow-sm"
                      name="clave_estado"
                      onChange={(e) =>
                        handleChange("clave_estado", e.target.value)
                      }
                      required
                    >
                      <option value="">Elegir...</option>
                      {estados.map((e) => (
                        <option key={e.clave_estado} value={e.clave_estado}>
                          {e.estado}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-4">
                    <label className="fw-bold mb-1 small text-muted">
                      Ciudad
                    </label>
                    <select
                      className="form-select shadow-sm"
                      name="clave_ciudad"
                      onChange={(e) =>
                        handleChange("clave_ciudad", e.target.value)
                      }
                      required
                      disabled={!proveedor.clave_estado}
                    >
                      <option value="">
                        {proveedor.clave_estado
                          ? "Elegir..."
                          : "Seleccione un estado primero"}
                      </option>
                      {ciudades.map((c) => (
                        <option key={c.clave_ciudad} value={c.clave_ciudad}>
                          {c.ciudad}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="text-end border-top pt-3">
                  <button
                    type="submit"
                    className="btn btn-success px-5 fw-bold shadow-sm"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>{" "}
                        Procesando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i> Guardar Corporativo
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProveedoresAlta;
